import { cva, type VariantProps } from 'class-variance-authority';
import { transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * AVATAR
 *
 * A person or company, as a photo with initials behind it. The fallback is the
 * common case rather than the exception here — most drivers have no photo — so
 * it is styled to look deliberate rather than like a broken image.
 */
export const avatarRecipe = cva(
  [uiMarker, 'relative flex shrink-0 overflow-hidden'],
  {
    variants: {
      /**
       * A person is a circle; a company is a rounded square. The distinction is
       * conventional enough that the sidebar and the settings list were both
       * overriding `rounded-pill` by hand to get it.
       */
      shape: {
        circle: 'rounded-pill',
        square: 'rounded-md',
      },
      size: {
        xs: 'size-6',
        sm: 'size-8',
        md: 'size-10',
        lg: 'size-12',
        xl: 'size-16',
      },
      /** A ring, to lift an avatar off a busy background such as the map. */
      ringed: {
        true: 'ring-2 ring-background',
        false: '',
      },
    },
    /*
     * A square avatar's corner scales with it — `rounded-md` on a 32px tile
     * reads as heavy-handed, which is why the sidebar dropdown hand-wrote
     * `rounded-sm` there and `rounded-md` on the 40px one above it.
     */
    compoundVariants: [
      { shape: 'square', size: 'xs', class: 'rounded-sm' },
      { shape: 'square', size: 'sm', class: 'rounded-sm' },
    ],
    defaultVariants: { shape: 'circle', size: 'md', ringed: false },
  }
);

export type AvatarRecipeProps = VariantProps<typeof avatarRecipe>;

export const avatarImageRecipe = cva([
  uiMarker,
  'aspect-square size-full object-cover',
]);

export const avatarFallbackRecipe = cva(
  [uiMarker, 'flex size-full items-center justify-center font-sans'],
  {
    variants: {
      shape: {
        circle: 'rounded-pill',
        square: 'rounded-md',
      },
      /**
       * `primary` is for the company you are currently acting as — the initials
       * are the only thing identifying it when there is no logo, so they carry
       * more weight than a muted placeholder would.
       */
      tone: {
        muted: 'bg-muted font-medium text-muted-foreground',
        primary: 'bg-primary font-semibold text-primary-foreground',
        /**
         * A company's initials on a card-coloured tile with a hairline: the
         * prospect's badge in the sales page header, where they have no logo.
         */
        outline: 'border border-border bg-card font-semibold text-primary',
      },
      size: {
        xs: 'text-overline',
        sm: 'text-caption',
        md: 'text-body-sm',
        lg: 'text-body',
        xl: 'text-h3',
      },
    },
    compoundVariants: [
      { shape: 'square', size: 'xs', class: 'rounded-sm' },
      { shape: 'square', size: 'sm', class: 'rounded-sm' },
    ],
    defaultVariants: { shape: 'circle', tone: 'muted', size: 'md' },
  }
);

/**
 * Overlapping row of avatars, for "assigned to these three drivers".
 * Negative margin on all but the first; the ring keeps them separated.
 */
export const avatarGroupRecipe = cva([
  uiMarker,
  'flex items-center [&>*:not(:first-child)]:-ml-2',
]);

/**
 * SCROLL AREA
 *
 * Native overflow with a styled scrollbar. Worth using where a default
 * scrollbar would be visually loud — side panels, long menus — not everywhere.
 */
export const scrollAreaRecipe = cva([uiMarker, 'relative overflow-hidden']);

/**
 * Radix wraps the content in a `display: table` box, which grows to the
 * widest child instead of the viewport. With only a vertical scrollbar that
 * extra width is cut off, unseen: a long line pushes every row past the
 * edge and `truncate` never fires. Vertical areas keep their content to the
 * viewport's width, so long text wraps or ends in an ellipsis.
 */
export const scrollViewportRecipe = cva(
  [uiMarker, uiMarkerLayout, 'size-full rounded-[inherit]'],
  {
    variants: {
      orientation: {
        vertical: '[&>div]:!block',
        horizontal: '',
      },
    },
    defaultVariants: { orientation: 'vertical' },
  }
);

export const scrollBarRecipe = cva(
  [uiMarker, 'flex touch-none select-none transition-colors duration-fast'],
  {
    variants: {
      orientation: {
        vertical: 'h-full w-2.5 border-l border-l-transparent p-[1px]',
        horizontal: 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      },
    },
    defaultVariants: { orientation: 'vertical' },
  }
);

export const scrollThumbRecipe = cva([
  uiMarker,
  'relative flex-1 rounded-pill bg-border hover:bg-muted-foreground/40',
]);

/**
 * FILE DROP TARGET
 *
 * The dashed border is the affordance — it reads as "put something here" in a
 * way a solid one does not. It brightens rather than changes shape while a
 * file is over it, so the target does not move under the cursor.
 */
export const dropzoneRecipe = cva(
  [
    uiMarker,
    'flex cursor-pointer flex-col items-center justify-center gap-1.5',
    'rounded-lg border border-dashed text-center',
    transitionBase,
  ],
  {
    variants: {
      active: {
        true: 'border-primary bg-primary/5',
        false: 'border-border hover:border-primary/40 hover:bg-muted/50',
      },
      height: {
        sm: 'h-24',
        md: 'h-32',
        lg: 'h-40',
        auto: 'p-6',
        /** A thumbnail-sized square at the end of a photo strip: a glyph over one word. */
        tile: 'size-20 gap-1 p-0',
      },
    },
    defaultVariants: { active: false, height: 'md' },
  }
);
export type DropzoneRecipeProps = VariantProps<typeof dropzoneRecipe>;
